import {useMemo, useCallback, useEffect} from 'react'
import {
  ModelPickerDesign,
  type AutoTierPickerConfig,
} from '@github-ui/copilot-shared-ui/components/chat-model-picker/ModelPickerDesign'
import {LOGO_BY_VENDOR, useAgentModels} from '@github-ui/copilot-shared-ui/agents/use-agent-models'
import type {CopilotChatModel} from '@github-ui/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import type {IconButtonProps} from '@primer/react'

export const AUTO_MODEL_ID = 'auto'

export interface ModelPickerProps {
  onUpdateModel: (modelId: string, options?: {reconcile?: boolean}) => void
  selectedModelId?: string
  /** When provided, only models whose vendor matches any of the filters (case-insensitive) are shown. */
  vendorFilters?: string[]
  /** Task payload-backed billing mode indicator. */
  isTokenBasedBillingEnabled?: boolean
  /** When false, the trigger hides its leading vendor/Copilot icon and shows the model name only. */
  showTriggerIcon?: boolean
  /** Optional trigger size; the env-strip toolbar passes 'medium' because Primer's
   *  ActionMenu.Button clips text at 'small' (28px). Other callers may use 'small'. */
  size?: 'small' | 'medium' | 'large'
  /** Keeps the strip trigger mounted with inert skeleton content while selection resolves. */
  showLoadingState?: boolean
  disabled?: boolean
  /** Opts the trigger into the composer's container-query compaction. */
  responsiveIconOnly?: boolean
  tooltipDirection?: IconButtonProps['tooltipDirection']
  /**
   * Controlled Auto-tier presentation. Omit it for unsupported Hosts, flag-off
   * states, coding-agent flows, or any other surface that must hide tier selection.
   */
  autoTierPicker?: AutoTierPickerConfig
}

function createAutoModel(vendorFilters?: string[]): CopilotChatModel {
  // Resolve the vendor name and logo so the auto model displays the agent's
  // brand icon (e.g. Claude logo, Codex logo) instead of the generic Copilot icon.
  let vendor = ''
  let logoURL: string | undefined
  if (vendorFilters) {
    for (const vf of vendorFilters) {
      for (const [key, url] of LOGO_BY_VENDOR) {
        if (key.toLowerCase() === vf.toLowerCase()) {
          vendor = key
          logoURL = url
          break
        }
      }
      if (logoURL) break
    }
  }

  return {
    id: AUTO_MODEL_ID,
    name: 'Auto',
    displayName: 'Auto',
    hasLimitedCapabilities: false,
    isThirdParty: false,
    version: '',
    vendor,
    logoURL,
    preview: false,
    model_picker_enabled: true,
    capabilities: {
      type: 'chat',
      family: '',
      limits: {max_prompt_tokens: 0},
      supports: {},
      tokenizer: '',
    },
  }
}

// brandAutoModel returns a copy of the auto model with vendor and logoURL set to
// match the agent's vendor, so ModelVendorIcon renders the correct brand icon.
function brandAutoModel(autoModel: CopilotChatModel, vendorFilterLower: string): CopilotChatModel {
  if (autoModel.logoURL) return autoModel
  for (const [key, url] of LOGO_BY_VENDOR) {
    if (key.toLowerCase() === vendorFilterLower) {
      return {...autoModel, vendor: key, logoURL: url}
    }
  }
  return autoModel
}

export function ModelPicker({
  onUpdateModel,
  selectedModelId,
  vendorFilters,
  isTokenBasedBillingEnabled,
  showTriggerIcon = true,
  size,
  showLoadingState = false,
  disabled = false,
  responsiveIconOnly,
  tooltipDirection,
  autoTierPicker,
}: ModelPickerProps) {
  const {models: allModels, isLoading} = useAgentModels()

  const availableModels = useMemo(() => {
    if (!allModels || !vendorFilters || vendorFilters.length === 0) return allModels
    const filtersLower = vendorFilters.map(v => v.toLowerCase())
    const filtered = allModels.filter(m => filtersLower.some(f => m.vendor?.toLowerCase().includes(f)))
    // Include the auto model from the API alongside vendor-filtered models,
    // so 3P agents (Claude, Codex) offer "Auto" just like the Copilot agent does.
    const autoModel = allModels.find(m => m.id === AUTO_MODEL_ID)
    if (filtered.length === 0) return autoModel ? [autoModel] : [createAutoModel(vendorFilters)]

    // Set the vendor logo on the auto model so it displays the agent's brand
    // icon (e.g. Claude logo, Codex logo) instead of the generic Copilot icon.
    const primaryVendor = filtersLower[0]
    const brandedAuto = autoModel && primaryVendor ? brandAutoModel(autoModel, primaryVendor) : null
    return brandedAuto ? [brandedAuto, ...filtered] : filtered
  }, [allModels, vendorFilters])

  const handleUpdateModel = useCallback(
    (model: CopilotChatModel) => {
      onUpdateModel(model.id)
    },
    [onUpdateModel],
  )

  const foundModel = availableModels?.find(model => model.id === selectedModelId)
  const defaultModel = availableModels?.find(m => m.is_chat_default) ?? availableModels?.[0]

  // When the selected model doesn't exist in the (vendor-filtered) list,
  // auto-select the default model so the picker doesn't show "(unavailable)".
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- loading keeps this component mounted for focus continuity, but unresolved vendor scope must not commit a fallback
    if (!showLoadingState && selectedModelId && availableModels && availableModels.length > 0 && !foundModel) {
      const fallback = availableModels.find(m => m.is_chat_default) ?? availableModels[0]
      if (fallback) {
        onUpdateModel(fallback.id, {reconcile: true})
      }
    }
  }, [showLoadingState, selectedModelId, availableModels, foundModel, onUpdateModel])

  // If no model selected yet, use default
  const selectedModel = selectedModelId ? (foundModel ?? defaultModel) : defaultModel

  // When no explicit selection exists and models have loaded, persist the resolved
  // default so that other submit paths (e.g. slash command, task entrypoint) that
  // read from localStorage will send the correct model instead of omitting it.
  const defaultModelId = defaultModel?.id
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- loading keeps this component mounted for focus continuity, but unresolved vendor scope must not commit a default
    if (!showLoadingState && !selectedModelId && defaultModelId && !isLoading) {
      onUpdateModel(defaultModelId, {reconcile: true})
    }
  }, [showLoadingState, selectedModelId, defaultModelId, isLoading, onUpdateModel])

  const isOnlyAutoModel =
    copilotFeatureFlags.codingAgentHideModelPickerIfOnlyAuto &&
    (!vendorFilters || vendorFilters.length === 0) &&
    availableModels?.length === 1 &&
    availableModels[0]?.id === AUTO_MODEL_ID

  if (showLoadingState) {
    return (
      <ModelPickerDesign
        disabled={disabled}
        type="global"
        variant="invisible"
        isLoading
        showTriggerIcon={false}
        size={size}
      />
    )
  }

  if (isLoading || !availableModels || !selectedModel || isOnlyAutoModel) return null

  return (
    <ModelPickerDesign
      models={availableModels}
      selectedModel={selectedModel}
      onModelPicked={handleUpdateModel}
      disabled={disabled}
      type={'global'}
      variant={'invisible'}
      isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
      showCategories
      showPreviewModelsSeparately={false}
      showTriggerIcon={showTriggerIcon}
      size={size}
      responsiveIconOnly={responsiveIconOnly}
      tooltipDirection={tooltipDirection}
      autoTierPicker={autoTierPicker}
    />
  )
}
